// src/pages/Partnership/JoinUsRequestPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { customerNoOf } from '../../components/CustomerReportShell';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';

// Areas offered in the customer app "Join With Us" form
const JOIN_US_CATEGORIES = ['Education', 'Healthcare', 'Environment', 'Other'];
const sectorLabel = (v) => {
  const t = String(v || '').trim();
  if (!t) return 'Other';
  const hit = JOIN_US_CATEGORIES.find((c) => c.toLowerCase() === t.toLowerCase());
  return hit || t.charAt(0).toUpperCase() + t.slice(1);
};

const JoinUsRequestPage = () => {
  const [tabValue, setTabValue] = useState(0); // 0: Pending, 1: History
  const { can } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [historyRequests, setHistoryRequests] = useState([]);

  // Filter states
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  const filterRequests = (list) => list.filter((item) => {
    if (selectedCategory !== 'ALL' && item.shopSector !== selectedCategory) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      const hit =
        (item.customerName && item.customerName.toLowerCase().includes(q)) ||
        (item.customerNo && String(item.customerNo).includes(q)) ||
        (item.contactNumber && String(item.contactNumber).toLowerCase().includes(q)) ||
        (item.shopSector && item.shopSector.toLowerCase().includes(q)) ||
        (item.description && item.description.toLowerCase().includes(q)) ||
        (item.answer && item.answer.toLowerCase().includes(q));
      if (!hit) return false;
    }
    return true;
  });

  // Inputs for answers
  const [answersMap, setAnswersMap] = useState({});
  const [submittingId, setSubmittingId] = useState(null);

  // Dialog state
  const [confirmModal, setConfirmModal] = useState({ open: false, req: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchPendingJoinUs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/partnership/joinUs/pending`);
      if (res.data?.status === 'SUCCESS') {
        setPendingRequests((res.data.requests || []).map((r) => ({ ...r, shopSector: sectorLabel(r.shopSector) })));
      }
    } catch (err) {
      console.error('Error fetching pending Join-Us requests:', err);
      showSnackbar('Error fetching pending Join-Us requests', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistoryJoinUs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/partnership/joinUs/history`);
      if (res.data?.status === 'SUCCESS') {
        setHistoryRequests((res.data.history || []).map((r) => ({ ...r, shopSector: sectorLabel(r.shopSector) })));
      }
    } catch (err) {
      console.error('Error fetching Join-Us request history:', err);
      showSnackbar('Error fetching Join-Us request history', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingJoinUs();
    fetchHistoryJoinUs();
  }, [fetchPendingJoinUs, fetchHistoryJoinUs]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 0) fetchPendingJoinUs();
    if (newValue === 1) fetchHistoryJoinUs();
  };

  const handleAnswerChange = (reqId, value) => {
    setAnswersMap(prev => ({ ...prev, [reqId]: value }));
  };

  const handleOpenConfirm = (req) => {
    if (!can('partnership.joinUs.complete')) return;
    const ans = (answersMap[req._id] || '').trim();
    if (!ans) {
      showSnackbar('Please enter an answer before completing', 'warning');
      return;
    }
    setConfirmModal({ open: true, req });
  };

  const handleExecuteComplete = async () => {
    if (!can('partnership.joinUs.complete')) return;
    const req = confirmModal.req;
    if (!req) return;

    setConfirmModal({ open: false, req: null });
    setSubmittingId(req._id);

    try {
      const answerText = answersMap[req._id] || 'Completed by Admin';
      const res = await axios.post(`${API_BASE_URL}/partnership/joinUs/complete`, {
        requestId: req._id,
        answer: answerText
      });

      if (res.data?.status === 'SUCCESS') {
        showSnackbar('Join-Us request marked as completed!');
        setAnswersMap(prev => {
          const updated = { ...prev };
          delete updated[req._id];
          return updated;
        });
        fetchPendingJoinUs();
        fetchHistoryJoinUs();
      } else {
        showSnackbar(res.data?.message || 'Failed to complete request', 'error');
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Error completing request';
      showSnackbar(msg, 'error');
    } finally {
      setSubmittingId(null);
    }
  };

  // CSV Export (permission gated)
  const handleExportCSV = () => {
    if (!can('partnership.joinUs.export')) return;
    const dataToExport = tabValue === 0 ? filterRequests(pendingRequests) : filterRequests(historyRequests);
    if (dataToExport.length === 0) {
      showSnackbar('No data to export', 'warning');
      return;
    }

    if (tabValue === 0) {
      const headers = ['SL No', 'Request Date & Time', 'Customer Name', 'Customer ID', 'Area to Join', 'Registered Contact Number', 'Description', 'Status'];
      const rows = dataToExport.map(item => [
        item.serialNumber,
        new Date(item.requestDate).toLocaleString('en-IN'),
        `"${item.customerName}"`,
        `"${customerNoOf(item)}"`,
        `"${item.shopSector}"`,
        `"${item.contactNumber}"`,
        `"${(item.description || '').replace(/"/g, '""')}"`,
        'Pending'
      ]);
      const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', `JOIN-US_Pending_Requests_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      const headers = ['SL No', 'Request Date & Time', 'Completed Date & Time', 'Customer Name', 'Customer ID', 'Area to Join', 'Registered Contact Number', 'Description', 'Show Answer', 'Status'];
      const rows = dataToExport.map(item => [
        item.serialNumber,
        new Date(item.requestDate).toLocaleString('en-IN'),
        new Date(item.completedDate).toLocaleString('en-IN'),
        `"${item.customerName}"`,
        `"${customerNoOf(item)}"`,
        `"${item.shopSector}"`,
        `"${item.contactNumber}"`,
        `"${(item.description || '').replace(/"/g, '""')}"`,
        `"${(item.answer || '').replace(/"/g, '""')}"`,
        'Completed'
      ]);
      const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', `JOIN-US_Request_History_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const filteredPending = filterRequests(pendingRequests);
  const filteredHistory = filterRequests(historyRequests);
  const currentRawList = tabValue === 0 ? pendingRequests : historyRequests;
  const allUniqueCategories = Array.from(new Set([...JOIN_US_CATEGORIES, ...[...pendingRequests, ...historyRequests].map((r) => r.shopSector).filter(Boolean)]));

  return (
    <Box sx={{ position: 'relative', padding: 3 }}>
      <BackButton />
      <br /><br />
      <Box sx={{ width: '100%', p: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'secondary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupAddIcon fontSize="large" color="secondary" />
            JOIN-US Request
          </Typography>

          {/* CSV Export Button (permission gated) */}
          {can('partnership.joinUs.export') && (
            <Button
              variant="contained"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleExportCSV}
              sx={{ fontWeight: 'bold', borderRadius: 2 }}
            >
              Export CSV
            </Button>
          )}
        </Box>

        {/* Filters Card */}
        <Paper elevation={2} sx={{ p: 2.5, mb: 3, borderRadius: 3, backgroundColor: '#ffffff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
            <FilterListIcon color="secondary" />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
              Filter Join-Us Requests
            </Typography>
          </Box>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Area to Join</InputLabel>
                <Select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} label="Area to Join">
                  <MenuItem value="ALL">All Areas</MenuItem>
                  {allUniqueCategories.map((cat, idx) => (
                    <MenuItem key={idx} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by Customer Name, ID, Mobile, Description, Answer..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{ startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} /> }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="outlined"
                color="secondary"
                size="small"
                fullWidth
                startIcon={<ClearIcon />}
                onClick={() => { setSelectedCategory('ALL'); setSearchQuery(''); }}
                sx={{ height: 40, fontWeight: 'bold' }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            <Chip
              label={`All: ${currentRawList.length}`}
              color={selectedCategory === 'ALL' ? 'secondary' : 'default'}
              onClick={() => setSelectedCategory('ALL')}
              sx={{ fontWeight: 'bold', cursor: 'pointer' }}
            />
            {allUniqueCategories.map((cat, idx) => (
              <Chip
                key={idx}
                label={`${cat}: ${currentRawList.filter((r) => r.shopSector === cat).length}`}
                color={selectedCategory === cat ? 'secondary' : 'default'}
                onClick={() => setSelectedCategory(cat)}
                sx={{ fontWeight: 'bold', cursor: 'pointer' }}
              />
            ))}
          </Box>
        </Paper>

        <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="secondary"
            textColor="secondary"
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={`Pending Requests (${filteredPending.length}/${pendingRequests.length})`} sx={{ fontWeight: 'bold' }} />
            <Tab label={`Request History (${filteredHistory.length}/${historyRequests.length})`} sx={{ fontWeight: 'bold' }} />
          </Tabs>

          {/* Tab 1: Pending Requests */}
          {tabValue === 0 && (
            <Box sx={{ p: 2 }}>
              <TableContainer sx={{ minHeight: 450, maxHeight: 'calc(100vh - 280px)', overflowX: 'auto' }}>
                <Table stickyHeader sx={{ minWidth: 1100 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', width: 70, whiteSpace: 'nowrap' }}>SL No</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 150, whiteSpace: 'nowrap' }}>Request Date & Time</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 160, whiteSpace: 'nowrap' }}>Show Customer Name</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 140, whiteSpace: 'nowrap' }}>Show Customer ID</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 180, whiteSpace: 'nowrap' }}>Show Area to Join</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 160, whiteSpace: 'nowrap' }}>Show Registered Contact number</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 260, maxWidth: 350, whiteSpace: 'nowrap' }}>Show Description</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 280, whiteSpace: 'nowrap' }}>Enter Answer</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : filteredPending.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No pending Join-Us requests match your filters</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredPending.map((row, idx) => (
                        <TableRow key={row._id || idx} hover>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {new Date(row.requestDate).toLocaleDateString('en-IN')}<br />
                            <Typography variant="caption" color="text.secondary">
                              {new Date(row.requestDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold', wordBreak: 'break-word' }}>{row.customerName}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}><Chip label={customerNoOf(row)} color="secondary" size="small" variant="outlined" /></TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: 'secondary.main', wordBreak: 'break-word' }}>{row.shopSector}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.contactNumber}</TableCell>
                          <TableCell sx={{ minWidth: 260, maxWidth: 350, wordBreak: 'break-word', whiteSpace: 'normal', pr: 3 }}>
                            {row.description}
                          </TableCell>
                          <TableCell sx={{ minWidth: 280 }}>
                            {can('partnership.joinUs.complete') ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  placeholder="Enter Answer / Resolution"
                                  multiline
                                  rows={2}
                                  value={answersMap[row._id] || ''}
                                  onChange={(e) => handleAnswerChange(row._id, e.target.value)}
                                />
                                <Button
                                  variant="contained"
                                  color="success"
                                  size="small"
                                  startIcon={submittingId === row._id ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                                  onClick={() => handleOpenConfirm(row)}
                                  disabled={submittingId === row._id}
                                  sx={{ fontWeight: 'bold' }}
                                >
                                  Complete Request
                                </Button>
                              </Box>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Tab 2: Request History */}
          {tabValue === 1 && (
            <Box sx={{ p: 2 }}>
              <TableContainer sx={{ minHeight: 450, maxHeight: 'calc(100vh - 280px)', overflowX: 'auto' }}>
                <Table stickyHeader sx={{ minWidth: 1100 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', width: 70, whiteSpace: 'nowrap' }}>SL No</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 150, whiteSpace: 'nowrap' }}>Request Date & Time</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 150, whiteSpace: 'nowrap' }}>Completed Date & Time</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 160, whiteSpace: 'nowrap' }}>Show Customer Name</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 140, whiteSpace: 'nowrap' }}>Show Customer ID</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 180, whiteSpace: 'nowrap' }}>Show Area to Join</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 160, whiteSpace: 'nowrap' }}>Show Registered Contact number</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 240, maxWidth: 320, whiteSpace: 'nowrap' }}>Show Description</TableCell>
                      <TableCell sx={{ backgroundColor: '#f1f5f9', fontWeight: 'bold', minWidth: 240, maxWidth: 320, whiteSpace: 'nowrap' }}>Show Answer</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : filteredHistory.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No completed Join-Us request history matches your filters</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredHistory.map((row, idx) => (
                        <TableRow key={row._id || idx} hover>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {new Date(row.requestDate).toLocaleDateString('en-IN')}<br />
                            <Typography variant="caption" color="text.secondary">
                              {new Date(row.requestDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {new Date(row.completedDate).toLocaleDateString('en-IN')}<br />
                            <Typography variant="caption" color="text.secondary">
                              {new Date(row.completedDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold', wordBreak: 'break-word' }}>{row.customerName}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}><Chip label={customerNoOf(row)} color="secondary" size="small" variant="outlined" /></TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: 'secondary.main', wordBreak: 'break-word' }}>{row.shopSector}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.contactNumber}</TableCell>
                          <TableCell sx={{ minWidth: 240, maxWidth: 320, wordBreak: 'break-word', whiteSpace: 'normal', pr: 2 }}>{row.description}</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: 'success.main', minWidth: 240, maxWidth: 320, wordBreak: 'break-word', whiteSpace: 'normal' }}>
                            {row.answer}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Paper>

        {/* Confirmation Modal */}
        <Dialog open={confirmModal.open} onClose={() => setConfirmModal({ open: false, req: null })} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ backgroundColor: 'secondary.main', color: 'white', fontWeight: 'bold' }}>
            Confirm Complete Join-Us Request
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
              <Typography variant="body1">
                <strong>Customer:</strong> {confirmModal.req?.customerName} ({confirmModal.req?.contactNumber})
              </Typography>
              <Typography variant="body1">
                <strong>Area to Join:</strong> {confirmModal.req?.shopSector}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body1">
                <strong>Answer to save:</strong>
              </Typography>
              <Typography variant="body2" sx={{ backgroundColor: '#f8fafc', p: 1.5, borderRadius: 1, border: '1px solid #cbd5e1' }}>
                {answersMap[confirmModal.req?._id]}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setConfirmModal({ open: false, req: null })} color="inherit">
              Cancel
            </Button>
            <Button onClick={handleExecuteComplete} variant="contained" color="success">
              Confirm & Complete
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default JoinUsRequestPage;
